import { useFormContext } from 'react-hook-form';

import { type ConnectionField } from '@/types/schema/fields';
import {
  type SearchView,
  type SearchViewFilterRule,
  type SearchViewInput
} from '@/types/schema/views/SearchView';
import { useConnectionRecordsQuery } from '@/hooks/api/queries/useConnectionRecordsQuery';
import { DependentConnectionInputPlaceholder } from '@/components/views/common/DependentConnectionInputPlaceholder';
import { useViewContext } from '@/components/views/ViewContext';
import { MultiChoiceBaseInput } from './MultiChoiceBaseInput';
import { useSearchViewParentInput } from './useSearchViewParentInput';

type ConnectionInputProps = {
  field: ConnectionField;
  intent: 'default' | 'destructive';
  formValueKey: string;
  searchViewFieldInput: SearchViewInput;
};

export function ConnectionInput({
  field,
  intent,
  formValueKey,
  searchViewFieldInput
}: ConnectionInputProps) {
  const { watch } = useFormContext();

  const { view } = useViewContext<SearchView>();

  const rules = watch('rules') as SearchViewFilterRule[];
  const { parentSearchViewFieldInput, parentInputSelectedRecordIds } = useSearchViewParentInput({
    input: searchViewFieldInput,
    rules,
    view
  });

  const { data: connectionRecords, isPending } = useConnectionRecordsQuery({
    viewKey: view.key,
    fieldKey: field.key,
    enabled: parentSearchViewFieldInput
      ? parentInputSelectedRecordIds && parentInputSelectedRecordIds.length > 0
      : true,
    parentInputSelectedRecordIds
  });

  if (
    parentSearchViewFieldInput &&
    parentInputSelectedRecordIds &&
    parentInputSelectedRecordIds.length === 0
  ) {
    return (
      <DependentConnectionInputPlaceholder
        fieldInputType={searchViewFieldInput.multi_input}
        parentInputName={parentSearchViewFieldInput.name}
      />
    );
  }

  return (
    <MultiChoiceBaseInput
      disabled={isPending}
      options={
        connectionRecords?.records.map((record) => ({
          label: record.identifier.toString(),
          value: record.id
        })) || []
      }
      formValueKey={formValueKey}
      multiType={searchViewFieldInput.multi_type}
      multiInput={searchViewFieldInput.multi_input}
      intent={intent}
    />
  );
}
